import { debounce } from 'lodash';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import {
  AdminService,
  MembershipService,
  formatDate,
  useApiEffect,
  useApiGetter,
  useGlobalSwr,
  useRoutes,
} from '@localstack/services';

import {
  COMPLETED_AUDIT_TRAIL_STATES,
  FAILED_AUDIT_TRAIL_STATES,
  LOADING_AUDIT_TRAIL_STATES,
} from '@localstack/constants';

import {
  AdminPermission,
  AdminSearchResult,
  AuditTrailQueryResult,
  AuditTrailQueryStatus,
  UserRole,
  User,
} from '@localstack/types';

import {
  AuditTrailTable,
  ButtonWithDialog,
  DangerZone,
  DangerZoneActions,
  LoadingFragment,
  ProgressButton,
  UserState,
} from '@localstack/ui';

import { AppRoute } from '~/config';
import { storeImpersonationMarker } from '~/util/storage';
import { AdminLayout } from '~/layouts';
import { useAuthProvider } from '~/hooks/useAuthProvider';

import { MemberForm } from './components/MemberForm';
import { AccountForm } from './components/AccountForm';
import { TosAcceptanceLog } from './components/TosAcceptanceLog';
import { LegalAcceptanceLog } from './components/LegalAcceptanceLog';

const SEARCH_DEBOUNCE_RATE = 1000;
const AUDIT_TRAIL_DEFAULT_START_OFFSET = 14 * 24 * 60 * 60; // 2 weeks (sec)

enum AccountTab {
  BASIC_DETAILS,
  MEMBERSHIPS,
  TERMS_AND_CONDITIONS,
  AUDIT_TRAIL,
  DANGER_ZONE,
}

const TabsDivider = styled(Divider)(({ theme }) => ({
  marginLeft: theme.spacing(-3),
  marginRight: theme.spacing(-3),
}));

const StyledMemberForm = styled(MemberForm)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

export const Account = (): ReactElement => {
  const { id: userId } = useParams<'id'>() as { id: string };
  const { goto } = useRoutes();

  const { reloadUserInfo, can } = useAuthProvider();

  const { clearAll: clearAllCache } = useGlobalSwr();

  const [currentTab, setCurrentTab] = useState<AccountTab>(AccountTab.BASIC_DETAILS);

  const [showMembershipForm, setShowMembershipForm] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [orgsQuery, setOrgsQuery] = useState<Optional<string>>(null);

  const [startTimestamp, setStartTimestamp] = useState(
    Math.floor(new Date().getTime() / 1000) - AUDIT_TRAIL_DEFAULT_START_OFFSET,
  );
  const [endTimestamp, setEndTimestamp] = useState(Math.floor(new Date().getTime() / 1000));

  const [auditTrailResult, setAuditTrailResult] = useState<AuditTrailQueryResult>({
    events: [],
    status: AuditTrailQueryStatus.COMPLETE,
  });

  const { data: userMemberships } = useApiGetter(MembershipService, 'adminListMemberships', [userId], {
    defaultValue: [],
  });

  const { data: user, isLoading: isAdminUserLoading } = useApiGetter(AdminService, 'getUser', [userId]);

  const {
    adminChangePrimaryMembership,
    adminDeleteMembership,
    adminCreateMembership,
    isLoading: isMembershipLoading,
  } = useApiEffect(
    MembershipService,
    ['adminChangePrimaryMembership', 'adminDeleteMembership', 'adminCreateMembership'],
    {
      revalidate: ['adminListMemberships'],
      revalidateOtherClient: { client: [AdminService], methods: ['getUser'] },
    },
  );

  const {
    updateUser,
    removeUser,
    activateUser,
    replaceUserEmail,
    isLoading: isAdminActionLoading,
  } = useApiEffect(AdminService, ['updateUser', 'removeUser', 'activateUser', 'replaceUserEmail'], {
    revalidate: ['getUser'],
  });

  const {
    resetUserPassword,
    assignUserPassword,
    resendUserActivation,
    isLoading: isPasswordLoading,
  } = useApiEffect(AdminService, ['resetUserPassword', 'resendUserActivation', 'assignUserPassword']);

  const { data: searchResults, isLoading: isSearching } = useApiGetter(AdminService, 'getSearchResults', [orgsQuery], {
    enable: !!orgsQuery,
    defaultValue: {} as AdminSearchResult,
  });

  const loadAuditTrail = useCallback(async () => {
    let result: AuditTrailQueryResult = {
      events: [],
      status: AuditTrailQueryStatus.SCHEDULED,
    };

    setAuditTrailResult(result);

    while (!COMPLETED_AUDIT_TRAIL_STATES.includes(result.status)) {
      try {
        result = await AdminService.getUserAuditTrail(userId, startTimestamp, result.query_id, endTimestamp);
      } catch (_e) {
        result = {
          status: AuditTrailQueryStatus.FAILED,
          events: [],
        };
      }

      setAuditTrailResult(result);

      await new Promise((t) => setTimeout(t, 3000));
    }
  }, [userId, startTimestamp, endTimestamp]);

  useEffect(() => {
    loadAuditTrail();
  }, [userId]);

  const handleImpersonateUser = useCallback(async (assumedUser: User) => {
    await clearAllCache(); // drop the swr cache as it may contain admin-user entries

    storeImpersonationMarker(assumedUser.id);

    await reloadUserInfo();

    goto(AppRoute.GETTING_STARTED);
  }, []);

  const isLoading =
    isAdminUserLoading || isMembershipLoading || isAdminActionLoading || isPasswordLoading || isSearching;

  return (
    <AdminLayout documentTitle="Account Details" title={user?.name ?? userId}>
      <Tabs
        value={currentTab}
        onChange={(_, tab: AccountTab) => setCurrentTab(tab)}
        indicatorColor="primary"
        textColor="primary"
        sx={{ marginTop: '-16px' }}
      >
        <Tab value={AccountTab.BASIC_DETAILS} label="Basic Details" />
        <Tab value={AccountTab.MEMBERSHIPS} label="Memberships" />
        <Tab value={AccountTab.TERMS_AND_CONDITIONS} label="Terms and Conditions" />
        <Tab value={AccountTab.AUDIT_TRAIL} label="Audit Trail" />
        {can(AdminPermission.USERS_WRITE) && <Tab value={AccountTab.DANGER_ZONE} label="Danger Zone" />}
      </Tabs>
      <TabsDivider />
      {currentTab === AccountTab.BASIC_DETAILS && (
        <Box mt={2}>
          <Box mt={2} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
            <Typography>
              <strong>State:&nbsp;</strong>
              {user && <UserState user={user} />}
            </Typography>
            <Typography>
              <strong>Activated:&nbsp;</strong>
              {user?.activated ? formatDate(user.activated) : 'N/A'}
            </Typography>
            <Typography>
              <strong>Source:&nbsp;</strong>
              {user?.how_found || 'N/A'}
            </Typography>
            <Typography>
              <strong># Organizations:&nbsp;</strong>
              {user?.organizations?.length || 0}
            </Typography>
          </Box>

          <Box mt={4}>
            <AccountForm user={user} loading={isLoading} onUpdate={updateUser} />
          </Box>
        </Box>
      )}
      {currentTab === AccountTab.MEMBERSHIPS && (
        <Box mt={2}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="caption" align="right" component="div">
              Memberships
            </Typography>
            <Box display="flex" alignItems="center">
              <IconButton
                color="primary"
                onClick={() => setShowMembershipForm(!showMembershipForm)}
                disabled={!can(AdminPermission.USERS_WRITE)}
              >
                <AddIcon />
              </IconButton>
            </Box>
          </Box>
          {showMembershipForm && (
            <StyledMemberForm
              loading={isLoading}
              searching={isSearching}
              searchedOrganizations={searchResults?.organizations ?? []}
              onSearchOrganization={debounce(setOrgsQuery, SEARCH_DEBOUNCE_RATE)}
              onCreateMember={async (orgId: string, role: UserRole) => {
                await adminCreateMembership(orgId, { user_id: user?.id as string, role });
                setShowMembershipForm(false);
              }}
            />
          )}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Organization</TableCell>
                <TableCell>Role</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {userMemberships.map((membership) => (
                <TableRow key={membership.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Link onClick={() => goto(AppRoute.ADMIN_ORGANIZATION, { id: membership.org_id })}>
                        {membership.org_name}
                      </Link>
                      {membership.is_primary && <Chip size="small" color="primary" label="Current" />}
                    </Box>
                  </TableCell>
                  <TableCell>{membership.membership_details.role}</TableCell>
                  <TableCell align="right">
                    <Box display="flex" justifyContent="flex-end" gap={1}>
                      {!membership.is_primary && (
                        <Button
                          variant="text"
                          size="small"
                          color="primary"
                          onClick={() =>
                            adminChangePrimaryMembership(membership.user_id, { org_id: membership.org_id })
                          }
                          disabled={isLoading || !can(AdminPermission.USERS_WRITE)}
                        >
                          Make Primary
                        </Button>
                      )}
                      <Button
                        variant="text"
                        size="small"
                        color="error"
                        onClick={() => adminDeleteMembership(membership.org_id, membership.user_id)}
                        disabled={isLoading || !can(AdminPermission.USERS_WRITE)}
                      >
                        Remove
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {currentTab === AccountTab.TERMS_AND_CONDITIONS && (
        <Box mt={2} display="flex" flexDirection="column" gap={2}>
          {user && <LegalAcceptanceLog user={user} />}
          {user && <TosAcceptanceLog user={user} />}
        </Box>
      )}
      {currentTab === AccountTab.AUDIT_TRAIL && (
        <Box mt={2}>
          <Box display="flex" mb={2}>
            <Box mr={1} flexGrow={1}>
              <TextField
                required
                variant="outlined"
                label="Start Date"
                size="small"
                type="date"
                fullWidth
                disabled={LOADING_AUDIT_TRAIL_STATES.includes(auditTrailResult.status)}
                onChange={({ target }) => setStartTimestamp(target.value ? moment(target.value).unix() : Date.now())}
                value={startTimestamp ? moment.unix(startTimestamp).format(moment.HTML5_FMT.DATE) : ''}
              />
            </Box>
            <Box mr={1} flexGrow={1}>
              <TextField
                required
                variant="outlined"
                label="End Date"
                size="small"
                type="date"
                fullWidth
                disabled={LOADING_AUDIT_TRAIL_STATES.includes(auditTrailResult.status)}
                onChange={({ target }) => setEndTimestamp(target.value ? moment(target.value).unix() : Date.now())}
                value={endTimestamp ? moment.unix(endTimestamp).format(moment.HTML5_FMT.DATE) : ''}
              />
            </Box>
            <ProgressButton
              variant="contained"
              color="primary"
              onClick={loadAuditTrail}
              disabled={LOADING_AUDIT_TRAIL_STATES.includes(auditTrailResult.status)}
              loading={LOADING_AUDIT_TRAIL_STATES.includes(auditTrailResult.status)}
            >
              Load Audit Trail
            </ProgressButton>
          </Box>
          {FAILED_AUDIT_TRAIL_STATES.includes(auditTrailResult.status) && (
            <Box mb={2}>
              <Alert severity="error">There was an error querying audit trail</Alert>
            </Box>
          )}
          <LoadingFragment
            loading={LOADING_AUDIT_TRAIL_STATES.includes(auditTrailResult.status)}
            arrayData={auditTrailResult.events ?? []}
            size={10}
            height={40}
          >
            <AuditTrailTable events={auditTrailResult.events ?? []} revealLocalStackStaff />
          </LoadingFragment>
        </Box>
      )}
      {can(AdminPermission.USERS_WRITE) && currentTab === AccountTab.DANGER_ZONE && (
        <Box mt={2}>
          {user && (
            <DangerZone>
              <DangerZoneActions>
                {!user.activated && (
                  <>
                    <ButtonWithDialog
                      componentType="DangerZoneAction"
                      primaryText="Resend Activation"
                      secondaryText="Resend activation link to the user"
                      actionText="Resend"
                      onClick={() => user && resendUserActivation(user.id)}
                      confirmButtonText="Resend"
                      dialogTitle="Resend Activation Link"
                      dialogContent="Are you sure you want to resend the activation link to this account?"
                    />
                    <ButtonWithDialog
                      componentType="DangerZoneAction"
                      primaryText="Activate the Account"
                      secondaryText="Mark this account as activated"
                      actionText="Activate"
                      onClick={() => user && activateUser(user.id)}
                      confirmButtonText="Activate"
                      dialogTitle="Activate the Account"
                      dialogContent="Are you sure you want to activate this account?"
                    />
                  </>
                )}
                <ButtonWithDialog
                  componentType="DangerZoneAction"
                  primaryText="Reset Password"
                  secondaryText="Send temporary password straight to the user"
                  actionText="Reset"
                  onClick={() => user && resetUserPassword(user.id)}
                  confirmButtonText="Reset"
                  dialogTitle="Reset Account Password"
                  dialogContent="Are you sure you want to reset the password for this account?"
                />
                <ButtonWithDialog
                  componentType="DangerZoneAction"
                  primaryText="Regenerate Password"
                  secondaryText="Generate a new password and show it here"
                  actionText="Regenerate"
                  onClick={async () => {
                    const result = await assignUserPassword(user.id);
                    alert(result.password);
                  }}
                  confirmButtonText="Regenerate Password"
                  dialogTitle="Regenerate password for the user"
                  dialogContent="Are you sure you want to generate a new password, it will no longer be possible to log in using an old one?"
                />
                <ButtonWithDialog
                  componentType="DangerZoneAction"
                  primaryText="Change User Email"
                  secondaryText="Change user email to another verified email"
                  actionText="Change"
                  confirmButtonText="Confirm"
                  onClick={() => replaceUserEmail(user.id, { email: newEmail })}
                  dialogTitle="Change User Email?"
                  dialogContent={
                    <>
                      <Typography paragraph>
                        Please note we are not doing any automated emails checks. Make sure user really owns the email
                        address before pressing the button.
                      </Typography>
                      <TextField
                        fullWidth
                        variant="outlined"
                        required
                        label="New Email"
                        onChange={(e) => setNewEmail(e.target.value)}
                        value={newEmail ?? ''}
                      />
                    </>
                  }
                />
                <ButtonWithDialog
                  componentType="DangerZoneAction"
                  primaryText="Impersonate User"
                  secondaryText="See the Platform how the user sees it"
                  actionText="Impersonate"
                  onClick={() => handleImpersonateUser(user)}
                  dialogTitle="Impersonate User?"
                  dialogContent={
                    <>
                      <Typography paragraph>
                        You will impersonate the user and be able to see the Platform the same way the user sees it.
                      </Typography>
                      <Typography paragraph color="error">
                        Remember that any changes made in this mode will take a real effect.
                      </Typography>
                    </>
                  }
                />
                <ButtonWithDialog
                  componentType="DangerZoneAction"
                  primaryText="Remove Account"
                  secondaryText="Mark User Account as Deleted"
                  actionText="Remove"
                  onClick={async () => {
                    await removeUser(userId);
                    goto(AppRoute.ADMIN_ACCOUNTS);
                  }}
                  dialogTitle="Remove this Account?"
                  dialogContent={
                    <>
                      The user will loose access to account. However, any data associated with this account will remain
                      unchanged, active keys and subscriptions will be retained.
                    </>
                  }
                />
              </DangerZoneActions>
            </DangerZone>
          )}
        </Box>
      )}
    </AdminLayout>
  );
};
